import React, { useContext } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Formik, Form, Field } from 'formik';
import { Button } from 'primereact/button';
import { Panel } from 'primereact/panel';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { InputTextWithValidationMessage, TextAreaWithValidation } from '../../../components/prime-formik';
import { NotificationContext } from '../../../components/GrowlNotifications';
import { AccountContext } from '../../../components/AccountContext';
import { growlErrors } from '../../../utils/NotificationUtil';

const i18n = defineMessages({
  transferFrom: { id: 'menu.transfer_from', defaultMessage: 'From: ' },
  transferToId: { id: 'menu.account_number', defaultMessage: 'Account number: ' },
  transferToName: { id: 'menu.account_name', defaultMessage: 'Account name: ' },
  transferSubmit: { id: 'menu.transfer_submit', defaultMessage: 'Transfer' },
  transferComputeDemo: { id: 'menu.compute_demo', defaultMessage: 'Compute Demo Credits' },
  transferAmount: { id: 'menu.transfer_amount', defaultMessage: 'Amount' },
  transferReason: { id: 'menu.transfer_reason', defaultMessage: 'Reason' },
  transferDebtorIdHint: {
    id: 'menu.transfer_debtor_id_hint',
    defaultMessage: 'Please enter an ID for an existing client account',
  },
  transferDebtorNameHint: {
    id: 'menu.transfer_debtor_name_hint',
    defaultMessage:
      'Please enter an ID for an existing client account and compute Demo credits for this to be completed',
  },
  transferReasonHint: { id: 'menu.transfer_reason_hint', defaultMessage: 'Please enter a reason for transfer.' },
  transferAmountHint: {
    id: 'menu.transfer_amount_hint',
    defaultMessage: 'Please enter a valid value greater than zero.',
  },
  contactSupport: {
    id: 'menu.transfer_support',
    defaultMessage:
      'Contact support@greatpeopleinside.zendesk.com is you want to transfer different amounts of demo credits',
  },
});

const TransferForm = ({ account, transferCredits, mutationResult: { loading } }) => {
  const { formatMessage } = useIntl();
  const history = useHistory();
  const currentAccount = useContext(AccountContext);
  const growl = useContext(NotificationContext);

  const createTransfer = async (values) => {
    try {
      await transferCredits({
        variables: {
          input: {
            creditorId: values.creditorId,
            debtorId: values.debtorId,
            reason: values.reason,
            amount: values.amount,
            isDemo: true,
          },
        },
      });

      history.push(`/accounts/${currentAccount}/demo-transfers`);
    } catch (err) {
      growlErrors(growl, err);
    }
  };

  const initialValues = {
    creditorId: currentAccount,
    debtorId: '',
    amount: 500,
    reason: '',
    isDemo: true,
  };

  const validationSchema = Yup.object().shape({
    isDemo: Yup.bool().isRequired,
    creditorId: Yup.string().isRequired,
    debtorId: Yup.string().required(formatMessage(i18n.transferDebtorIdHint)),
    reason: Yup.string().required(formatMessage(i18n.transferReasonHint)),
    amount: Yup.number().required(formatMessage(i18n.transferAmountHint)),
  });

  return (
    <div className="p-grid">
      <div className="p-col-12">
        <div className="brand-detailed-wrapper">
          <Panel>
            <div className="p-grid">
              <div className="p-sm-12 p-md-6">
                <Formik
                  enableReinitialize
                  validationSchema={validationSchema}
                  initialValues={initialValues}
                  onSubmit={(values, formikBag) => {
                    const { resetForm } = formikBag;
                    createTransfer(values);
                    resetForm();
                  }}
                  isSubmitting={loading}
                >
                  {({ values, handleChange, isSubmitting }) => (
                    <Form>
                      <div className="p-grid p-fluid">
                        <div className="p-col-12">
                          <label>{formatMessage(i18n.transferFrom)}</label>
                          <Field
                            component={InputTextWithValidationMessage}
                            id="creditorId"
                            name="creditorId"
                            key="creditorId"
                            value={account.displayName}
                            readOnly
                          />
                        </div>

                        <div className="p-col-12">
                          <label>{formatMessage(i18n.transferToId)}</label>
                          <Field
                            component={InputTextWithValidationMessage}
                            id="debtorId"
                            name="debtorId"
                            key="debtorId"
                            type="number"
                            onChange={handleChange}
                          />
                        </div>

                        <div className="p-col-12">
                          <label>{formatMessage(i18n.transferAmount)}</label>
                          <Field
                            component={InputTextWithValidationMessage}
                            id="amount"
                            name="amount"
                            type="number"
                            value={values.amount}
                            readOnly
                          />
                        </div>

                        <div className="p-col-12">
                          <label>{formatMessage(i18n.transferReason)}</label>
                          <Field
                            component={TextAreaWithValidation}
                            id="reason"
                            name="reason"
                            value={values.reason}
                            onChange={handleChange}
                          />
                        </div>
                      </div>
                      <div className="p-grid">
                        <div className="p-col-12 flex">
                          <div>
                            <span className="pi pi-info-circle font-size-1-5"></span>
                          </div>
                          <div>
                            <strong>{formatMessage(i18n.contactSupport)}</strong>
                          </div>
                        </div>
                      </div>
                      <br />
                      <div className="p-grid">
                        <div className="p-col-12">
                          <Button label={formatMessage(i18n.transferSubmit)} type="submit" disabled={isSubmitting} />
                        </div>
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
};

const accountShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  displayName: PropTypes.string.isRequired,
});

TransferForm.propTypes = {
  account: accountShape.isRequired,
  transferCredits: PropTypes.func.isRequired,
  mutationResult: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
    error: PropTypes.objectOf(PropTypes.any),
  }).isRequired,
};

export default TransferForm;
